import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchYoungProfRsvps,
  deleteYoungProfRsvp,
  exportYoungProfRsvpCsv,
} from '../../api/client';
import formatDate from '../../utils/formatDate';
import './YoungProfRsvpList.css';

const ATTENDANCE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'attending', label: 'Attending' },
  { value: 'not_attending', label: 'Not Attending' },
];

function attendanceBadge(status) {
  if (status === 'attending') return <span className="badge badge-positive">Attending</span>;
  if (status === 'not_attending') return <span className="badge badge-negative">Not Attending</span>;
  return <span className="badge badge-neutral">No Answer</span>;
}

function outcomeBadgeClass(outcome) {
  if (outcome === 'completed') return 'badge-positive';
  if (outcome === 'partially_completed') return 'badge-pending';
  return 'badge-negative';
}

export default function YoungProfRsvpList() {
  const [rsvps, setRsvps] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [skip, setSkip] = useState(0);
  const [attendanceFilter, setAttendanceFilter] = useState('');
  const limit = 20;

  const loadRsvps = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit, skip };
      if (attendanceFilter) params.attendance_status = attendanceFilter;
      const res = await fetchYoungProfRsvps(params);
      setRsvps(res.data || []);
      setTotal(res.meta?.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [skip, attendanceFilter]);

  useEffect(() => {
    loadRsvps();
  }, [loadRsvps]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (confirmId === id) {
      try {
        await deleteYoungProfRsvp(id);
        setConfirmId(null);
        loadRsvps();
      } catch (err) {
        setError(err.message);
      }
    } else {
      setConfirmId(id);
    }
  };

  const handleCancelDelete = (e) => {
    e.stopPropagation();
    setConfirmId(null);
  };

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await exportYoungProfRsvpCsv({ attendance_status: attendanceFilter });
    } catch (err) {
      setExportError(err.message);
    } finally {
      setExporting(false);
    }
  };

  if (loading && rsvps.length === 0) {
    return <div className="loading">Loading RSVPs...</div>;
  }

  if (error) {
    return <div className="error-message">Failed to load RSVPs: {error}</div>;
  }

  const hasMore = skip + limit < total;
  const hasPrev = skip > 0;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <h3 style={{ margin: 0 }}>Young Prof RSVPs</h3>
        <button className="btn-secondary" onClick={handleExport} disabled={exporting}>
          {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>
      {exportError && (
        <div className="error-message" style={{ marginBottom: '0.5rem' }}>
          Export failed: {exportError}
        </div>
      )}
      <div className="young-prof-rsvp-filter">
        <label htmlFor="ypf-attendance-filter">Filter by attendance:</label>
        <select
          id="ypf-attendance-filter"
          value={attendanceFilter}
          onChange={(e) => { setAttendanceFilter(e.target.value); setSkip(0); }}
        >
          {ATTENDANCE_OPTIONS.map((o) => (
            <option key={o.value || 'all'} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {rsvps.length === 0 ? (
        <p>No RSVPs recorded yet.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Phone</th>
              <th>Attendance</th>
              <th>Outcome</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rsvps.map((r) => (
              <tr key={r._id}>
                <td>{formatDate(r.created_at)}</td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  {r.phone_number || 'N/A'}
                </td>
                <td>{attendanceBadge(r.attendance_status)}</td>
                <td>
                  <span className={`badge ${outcomeBadgeClass(r.call_outcome)}`}>
                    {(r.call_outcome || 'unknown').replace('_', ' ')}
                  </span>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {confirmId === r._id ? (
                    <>
                      <button className="btn-danger-sm" onClick={(e) => handleDelete(e, r._id)}>Confirm</button>
                      {' '}
                      <button className="btn-secondary-sm" onClick={handleCancelDelete}>Cancel</button>
                    </>
                  ) : (
                    <button className="btn-icon" onClick={(e) => handleDelete(e, r._id)} title="Delete">&#128465;</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {(hasPrev || hasMore) && (
        <div className="submission-pagination">
          <button
            className="btn-secondary"
            disabled={!hasPrev}
            onClick={() => setSkip((prev) => Math.max(0, prev - limit))}
          >
            Previous
          </button>
          <span className="pagination-info">
            {total === 0 ? 0 : skip + 1}–{Math.min(skip + limit, total)} of {total}
          </span>
          <button
            className="btn-secondary"
            disabled={!hasMore}
            onClick={() => setSkip((prev) => prev + limit)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
